/**
 * AuthContext — client-side mirror of the backend RBAC chain.
 *
 * Source of truth is GET /api/company/entitlements, which returns the caller's
 * membership role, the company plan's entitled features + limits, live usage,
 * per-member grants, and the platform-admin flag. This context exposes helpers
 * (can, hasRole, isPlatformAdmin) so components can hide/disable what the user
 * cannot do. The backend still enforces everything — this is UX gating only.
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import authService from '../services/auth';
import api from '../services/api';

// --- role/permission matrix (mirror of api/auth/rbac.py) ---
const ROLE_RANK = { viewer: 0, member: 1, admin: 2, owner: 3 };
const DEFAULT_ACTION_ROLE = {
  view: 'viewer', create: 'member', edit: 'member', delete: 'admin', manage: 'admin',
};
const FEATURE_OVERRIDES = {
  team_management: { view: 'member', create: 'admin', edit: 'admin', delete: 'admin', manage: 'admin' },
  api_keys: { view: 'admin', create: 'admin', edit: 'admin', delete: 'admin', manage: 'admin' },
  company_settings: { view: 'member', create: 'admin', edit: 'admin', delete: 'owner', manage: 'owner' },
  billing: { view: 'admin', create: 'owner', edit: 'owner', delete: 'owner', manage: 'owner' },
};
const roleRank = (r) => (r && r in ROLE_RANK ? ROLE_RANK[r] : -1);
const requiredRoleFor = (feature, action) => {
  const o = FEATURE_OVERRIDES[feature];
  if (o && o[action]) return o[action];
  return DEFAULT_ACTION_ROLE[action] || 'admin';
};
// Features whose availability depends on the plan (used by can()/hasFeature).
const FEATURE_GOVERNED = new Set([
  'chat', 'knowledge_base', 'widgets', 'conversations', 'analytics',
  'voice', 'file_upload', 'api_keys', 'team_management', 'company_settings',
]);

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [entitlements, setEntitlements] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!authService.isLoggedIn()) {
      setEntitlements(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get('/api/company/entitlements');
      setEntitlements(res.data);
    } catch (e) {
      // 403 (no company) or network error — treat as no entitlements.
      setEntitlements(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const role = entitlements?.role || null;
  const isPlatformAdmin = !!entitlements?.is_platform_admin;
  const features = entitlements?.features || [];
  const grants = entitlements?.grants || {};

  const hasRole = useCallback(
    (minRole) => isPlatformAdmin || roleRank(role) >= roleRank(minRole),
    [role, isPlatformAdmin]
  );

  const can = useCallback(
    (feature, action = 'view') => {
      if (isPlatformAdmin) return true;
      if (!entitlements) return false;
      if (!entitlements.is_active) return false;
      // L2 plan entitlement: block features the plan governs but does not include
      if (FEATURE_GOVERNED.has(feature) && !features.includes(feature)) return false;
      // L3 per-member grant overrides the role default
      const g = grants[feature];
      if (g && action in g) return !!g[action];
      return roleRank(role) >= roleRank(requiredRoleFor(feature, action));
    },
    [entitlements, features, grants, role, isPlatformAdmin]
  );

  const value = {
    entitlements, loading, role, isPlatformAdmin, features,
    limits: entitlements?.limits || {}, usage: entitlements?.usage || {},
    planSlug: entitlements?.plan_slug || null, planName: entitlements?.plan_name || null,
    hasRole, can, refresh,
    hasFeature: (f) => isPlatformAdmin || features.includes(f),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}

export default AuthContext;
